










































































































































































import Vue from "vue";
import dayjs from "dayjs";
import eventBus from "@/services/eventBus";
import base64toUInt8Array, { openPdfInNewTab } from "@/utils/fileConvert";
import DateInput from "@/components/DateInput.vue";
import api from "../services/api";
import {
  RunCostMatrixParams,
  DefaultRunCostMatrixParams,
  CostMatrixResult,
} from "../models/CostMatrix";
import { ReportHeadOption, ReportAccountCheckRow } from "../models/ReportRun";
import { t } from "@/plugins/i18n";

interface Data {
  params: RunCostMatrixParams;
  accountCheck: boolean;
  issueDate: string;
  reportHeadOptions: ReportHeadOption[];
  checkRows: ReportAccountCheckRow[];
  matrix: CostMatrixResult;
  runId: number;
  running: boolean;
  exporting: boolean;
  checkColumns: any[];
}
interface Methods {
  loadReportHeads(): Promise<void>;
  run(): Promise<void>;
  doRun(): Promise<void>;
  printPdf(): Promise<void>;
  exportExcel(): Promise<void>;
  reset(): void;
  formatValue(v: number | null): string;
  formatDate(v: string): string;
  rowStyle(row: any): object;
}
interface Computed {
  gridColumns: any[];
  gridRows: any[];
  headerTooltips: Record<string, string>;
}

function emptyMatrix(): CostMatrixResult {
  return { columns: [], rows: [], columnTotals: [], grandTotal: 0 };
}

export default Vue.extend<Data, Methods, Computed, {}>({
  components: { DateInput },

  data(): Data {
    return {
      params: { ...DefaultRunCostMatrixParams },
      accountCheck: true,
      issueDate: dayjs().format("YYYY-MM-DD"),
      reportHeadOptions: [],
      checkRows: [],
      matrix: emptyMatrix(),
      runId: 0,
      running: false,
      exporting: false,
      checkColumns: [
        { field: "accno", title: t("GeneralLedger_Fksz"), width: "110px" },
        { field: "partnerName", title: t("Partner"), width: "220px" },
        { field: "accDate", title: t("Datum"), width: "110px", cell: "checkDateTemplate" },
        { field: "accSign", title: "T/K", width: "60px" },
        { field: "total", title: t("Osszeg"), width: "130px", cell: "checkTotalTemplate" },
        { field: "transId", title: t("GeneralLedger_Tranzakcio"), width: "110px" },
        { field: "note", title: t("Megjegyzes") },
      ],
    };
  },

  async beforeMount() {
    await this.loadReportHeads();
  },

  computed: {
    gridColumns(): any[] {
      const nameWidth = this.matrix.columns.length > 6 ? "280px" : undefined;
      const cols: any[] = [
        { field: "code", title: t("Kod"), width: "120px", cell: "codeTemplate" },
        { field: "name", title: t("Megnevezes"), width: nameWidth, cell: "nameTemplate" },
      ];
      this.matrix.columns.forEach((c, i) => {
        cols.push({
          field: `c${i}`,
          title: c.code || `(${c.id})`,
          width: "130px",
          cell: "valueTemplate",
          headerCell: "dimHeaderTemplate",
        });
      });
      if (this.matrix.columns.length > 0) {
        cols.push({ field: "rowTotal", title: t("Osszesen"), width: "140px", cell: "valueTemplate" });
      }
      return cols;
    },

    gridRows(): any[] {
      const rows = this.matrix.rows.map(r => {
        const flat: any = {
          code: r.code,
          name: r.name,
          isCode: r.isCode,
          isBold: r.isBold,
          isItalic: r.isItalic,
          isTotalRow: false,
          rowTotal: r.rowTotal,
        };
        r.cells.forEach((value, i) => {
          flat[`c${i}`] = value;
        });
        return flat;
      });
      if (rows.length > 0) {
        const total: any = {
          code: "",
          name: t("Osszesen"),
          isCode: false,
          isBold: true,
          isItalic: false,
          isTotalRow: true,
          rowTotal: this.matrix.grandTotal,
        };
        this.matrix.columnTotals.forEach((value, i) => {
          total[`c${i}`] = value;
        });
        rows.push(total);
      }
      return rows;
    },

    headerTooltips(): Record<string, string> {
      const dict: Record<string, string> = {};
      this.matrix.columns.forEach((c, i) => {
        dict[`c${i}`] = c.name || "";
      });
      return dict;
    },
  },

  methods: {
    async loadReportHeads() {
      this.reportHeadOptions = await api.GetReportHeads(5);
      if (this.reportHeadOptions.length === 0) {
        eventBus.$emit("error", t("GeneralLedger_NincsKoltsegmatrixTipusuRiportDefinicio"));
        return;
      }
      this.params.reportHeadId = this.reportHeadOptions[0].id;
    },

    async run() {
      if (!this.params.reportHeadId || this.params.reportHeadId <= 1) {
        eventBus.$emit("error", t("GeneralLedger_ValasszonRiportot"));
        return;
      }
      if (!this.params.fromDate || !this.params.toDate) {
        eventBus.$emit("error", t("GeneralLedger_AdjaMegAzIntervallumot"));
        return;
      }

      eventBus.$emit("busy-message", true);
      try {
        if (this.accountCheck) {
          try {
            this.checkRows = await api.GetReportAccountCheck({
              reportHeadId: this.params.reportHeadId,
              mode: "X",
              fromDate: this.params.fromDate,
              toDate: this.params.toDate,
            });
          } catch (error) {
            console.error(error);
            eventBus.$emit("error", t("GeneralLedger_AzFkszEllenorzesNemSikerult"));
            return;
          }
          if (this.checkRows.length > 0) {
            eventBus.$emit(
              "error",
              t("GeneralLedger_FkszEllenorzesKonyveltTetelNemEsik_2", [this.checkRows.length])
            );
            return;
          }
        } else {
          this.checkRows = [];
        }

        await this.doRun();
      } finally {
        eventBus.$emit("busy-message", false);
      }
    },

    async doRun() {
      this.running = true;
      try {
        const result = await api.RunCostMatrix(this.params);
        if (!result.success) {
          eventBus.$emit("error", result.message || t("GeneralLedger_AKoltsegmatrixFuttatasaNemSikerult"));
          return;
        }
        this.runId = result.runId;
        this.matrix = await api.GetCostMatrixRows(this.runId);
        if (this.matrix.rows.length === 0) {
          eventBus.$emit("warn", t("GeneralLedger_AMatrixNemTartalmazAdatotA"));
        }
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", t("GeneralLedger_AKoltsegmatrixFuttatasaNemSikerult"));
      } finally {
        this.running = false;
      }
    },

    async printPdf() {
      this.exporting = true;
      eventBus.$emit("busy-message", true);
      try {
        const base64 = await api.GetCostMatrixPdf(this.runId);
        if (!base64) {
          eventBus.$emit("error", t("GeneralLedger_AMatrixNyomtatasaNemSikerult"));
          return;
        }
        openPdfInNewTab(base64toUInt8Array(base64));
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", t("GeneralLedger_AMatrixNyomtatasaNemSikerult"));
      } finally {
        this.exporting = false;
        eventBus.$emit("busy-message", false);
      }
    },

    async exportExcel() {
      this.exporting = true;
      eventBus.$emit("busy-message", true);
      try {
        const file = await api.GetCostMatrixExcel(this.runId);
        if (!file) {
          eventBus.$emit("error", t("GeneralLedger_AzExcelExportNemSikerult"));
          return;
        }
        const downloadLink = document.createElement("a");
        downloadLink.href = `data:application/vnd.openxmlformats;base64,${file}`;
        downloadLink.download = `Koltsegmatrix_${this.runId}.xlsx`;
        downloadLink.click();
      } catch (error) {
        console.error(error);
        eventBus.$emit("error", t("GeneralLedger_AzExcelExportNemSikerult"));
      } finally {
        this.exporting = false;
        eventBus.$emit("busy-message", false);
      }
    },

    reset() {
      const reportHeadId = this.reportHeadOptions.length > 0 ? this.reportHeadOptions[0].id : 0;
      this.params = { ...DefaultRunCostMatrixParams, reportHeadId };
      this.accountCheck = true;
      this.issueDate = dayjs().format("YYYY-MM-DD");
      this.checkRows = [];
    },

    formatValue(v: number | null): string {
      if (v === null || v === undefined) return "";
      return v.toLocaleString("hu-HU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    formatDate(v: string): string {
      if (!v) return "";
      return dayjs(v).format("YYYY-MM-DD");
    },

    rowStyle(row: any): object {
      return {
        fontWeight: row.isBold || row.isTotalRow ? 700 : 400,
        fontStyle: row.isItalic ? "italic" : "normal",
      };
    },
  },
});
